import { getActionName } from '@app/core/state/state-helpers';
import {
  createAction,
  createActionGroup,
  emptyProps,
  props,
} from '@ngrx/store';
import { AccountSummary } from '@app/fin-ops/models/responses/account-summary';
import { LoadTrialBalanceAccountsRequest } from '@app/fin-ops/models/requests/load-trial-balance-accounts-request';
import { LoadTrialBalanceTransactionsRequest } from '@app/fin-ops/models/requests/load-trial-balance-transactions-request';
import { GeneratedReport } from '@app/fin-ops/models/generated-report';
import { TrialBalanceAccount } from '@app/fin-ops/models/responses/trial-balance-account';
import { LoadDetailedTrialBalanceTransactionsRequest } from '@app/fin-ops/models/requests/load-detailed-trial-balance-transactions-request';
import { LoadTrialBalanceTransactionsTotalsRequest } from '@app/fin-ops/models/requests/load-trial-balance-transactions-totals-request';
import { TrialBalanceTransactionsFilters } from '@app/fin-ops/models/responses/trial-balance-transactions-filters';
import { TrialBalanceTransactionsTotals } from '@app/fin-ops/models/responses/trial-balance-transactions-totals';
import { ExportTrialBalanceTransactionsRequest } from '@app/fin-ops/models/requests/export-trial-balance-transactions-request';
import { LoadMonthEndReadinessTableRequest } from '@app/fin-ops/models/requests/load-month-end-readiness-table-request';
import { ReconcileTransactionsRequest } from '@app/fin-ops/models/requests/reconcile-transactions-request';
import { LoadMonthEndReadinessCardsRequest } from '@app/fin-ops/models/requests/load-month-end-readiness-cards-request';
import { TransactionReconciliationSummary } from '@app/fin-ops/models/responses/transaction-reconciliation-summary';

import { LedgerFilters } from '@app/fin-ops/models/responses/ledger-filters';
import { BankFilters } from '@app/fin-ops/models/responses/bank-filters';
import { ExportMonthEndReadinessTableRequest } from '@app/fin-ops/models/requests/export-month-end-readiness-table-request';
import { TransactionReconciliation } from '@app/fin-ops/models/responses/transaction-reconciliation';
import { UnmatchTransactionsRequest } from '@app/fin-ops/models/requests/unmatch-transactions-request';
import { LoadBankReconciliationSummaryRequest } from '@app/fin-ops/models/requests/load-bank-reconciliation-summary-request';
import { BankReconciliationSummary } from '@app/fin-ops/models/responses/bank-reconciliation-summary';
import { MonthEndDates } from '@app/fin-ops/models/responses/month-end-dates';
import { AgGridRequest } from '@app/shared/models/ag-grid-request';
import { SummaryBankTransaction } from '@app/fin-ops/models/responses/summary-bank-transaction';
import { SummaryLedgerTransaction } from '@app/fin-ops/models/responses/summary-ledger-transaction';
import { MatchedBankTransaction } from '@app/fin-ops/models/responses/matched-bank-transaction';
import { MatchedLedgerTransaction } from '@app/fin-ops/models/responses/matched-ledger-transaction';
import { MatchedTransaction } from '@app/fin-ops/models/responses/matched-transaction';
import { AllTransactionReconciliation } from '@app/fin-ops/models/responses/all-transaction-reconciliation';
import { UnmatchedLedgerTransaction } from '@app/fin-ops/models/responses/unmatched-ledger-transaction';
import { UnmatchedBankTransaction } from '@app/fin-ops/models/responses/unmatched-bank-transaction';
import { SummaryDrillThroughMetadata } from '@app/fin-ops/models/responses/summary-drill-through-metadata';
import { LoadBankReconciliationSummaryDrillThroughMetadataRequest } from '@app/fin-ops/models/requests/load-bank-reconciliation-summary-drill-through-metadata-request';
import { UnearnedPremiumReserve } from '@app/fin-ops/models/responses/unearned-premium-reserve';
import { LoadUnearnedPremiumReserveRequest } from '@app/fin-ops/models/requests/load-unearned-premium-reserve-request';
import { UnearnedPremiumReserveTotals } from '@app/fin-ops/models/responses/unearned-premium-reserve-totals';
import { LoadUnearnedPremiumReserveTotalsRequest } from '@app/fin-ops/models/requests/load-unearned-premium-reserve-totals-request';
import { ExportBankReconciliationSummaryDrillThroughRequest } from '@app/fin-ops/models/requests/export-bank-reconciliation-summary-drill-through-request';
import { LoadOutstandingClaimsReportRequest } from '@app/fin-ops/models/requests/load-outstanding-claims-report-request';
import { OutstandingClaimReserve } from '@app/fin-ops/models/responses/outstanding-claim-reserve';
import { AllPostingRequests } from '@app/fin-ops/models/responses/all-posting-requests';
import { AllPostingLedgerTransactions } from '@app/fin-ops/models/responses/all-posting-ledger-transactions';
import { AllPostingDraftTransactions } from '@app/fin-ops/models/responses/all-posting-draft-transactions';
import { PostingRequest } from '@app/fin-ops/models/responses/posting-request';
import { CreatePostingRequest } from '@app/fin-ops/models/requests/create-posting-request';
import { UpdatePostingRequest } from '@app/fin-ops/models/requests/update-posting-request';
import { ActionPostingRequest } from '@app/fin-ops/models/requests/action-posting-request';
import { PostingMetadata } from '@app/fin-ops/models/responses/posting-metadata';
import { ReversePostingRequest } from '@app/fin-ops/models/requests/reverse-posting-request';
import { PostingExportFilters } from '@app/fin-ops/models/requests/posting-export-filters';
import { LoadClaimsMovementReportRequest } from '@app/fin-ops/models/requests/load-claims-movement-report-request';
import { ClaimMovement } from '@app/fin-ops/models/responses/claim-movement';
import { ClaimMovementTotals } from '@app/fin-ops/models/responses/claim-movement-totals';
import { PaymentRejections } from '@app/fin-ops/models/responses/payment-rejections';
import {
  ComplexSearchRequest,
  SearchRequest,
} from '@app/shared/search/interfaces/search-request';
import { SortPropDir } from '@swimlane/ngx-datatable';
import { PagedApiData } from '@app/core/models/PagedApiData';
import { RejectedPaymentEapDetails } from '@app/fin-ops/models/responses/rejected-payment-eap-details';
import { RejectedPaymentItemDecisionType } from '@app/fin-ops/models/enums/rejected-payment-item-decision-type';
import { PaymentNotification } from '@app/fin-ops/models/responses/payment-notification';
import { PaymentItemRejectionType } from '@app/fin-ops/models/responses/payment-item-rejection-type';
import { ActionRejectedPaymentItemsRequest } from '@app/fin-ops/models/requests/action-rejected-payment-items-request';
import { ApprovalStepEnum } from '@app/fin-ops/models/enums/approval-step.enum';
import { RejectionModalSummary } from '@app/fin-ops/models/responses/rejection-modal-summary';
import { PaymentItemResubmissionCount } from '@app/fin-ops/models/responses/payment-item-resubmission-count';
import { LoadBankStatementRequest } from '@app/fin-ops/models/requests/load-bank-statement-request';
import { PaymentRejectionsAction } from '../models/responses/payment-rejections-action';
import { PaymentRejectionReferralUser } from '../models/enums/payment-rejection-referral-user';
import { RejectedPayment } from '../models/rejected-payment';
import { LatestBankStatementDetails } from '../models/responses/latest-bank-statement-details';
import { OutstandingPaymentsDetails } from '../models/responses/outstanding-payments-details';
import { Payment } from '../models/responses/payment';
import { ReleasePaymentsRequest } from '../models/responses/release-payments-request';
import { OutstandingBatchDetails } from '../models/responses/outstanding-batch-details';
import { GetOutstandingBatchDetails } from '../models/requests/getOutstandingBatchDetails';
import { PaymentReasonCodeFilter } from '../models/responses/payment-reason-code-filter';
import { BatchSummary } from '../models/responses/batch-summary';
import { BatchDetails } from '../models/responses/batch-details';
import { BatchSummaryItem } from '../models/responses/batch-summary-item';
import { BatchActionHistoryRequest } from '../models/requests/batch-action-history.request';
import { ActionPaymentReleaseApproval } from '../models/requests/action-payment-release-approval';
import { InProgressBatch } from '../models/responses/in-progress-batch';
import { ReleaseOverviewSummary } from '../models/responses/release-overview-summary';
import { ReleaseOverviewTransactionRequest } from '../models/requests/release-overview-transaction-request';
import { ReleaseOverviewPaymentsRequest } from '../models/requests/release-overview-payments-request';
import { BankRejectionSummaryResponse } from '../models/responses/bank-rejection-summary-response';
import { BankRejectionResponse } from '../models/responses/bank-rejection-response';
import { BankRejectionHistoryResponse } from '../models/responses/bank-rejection-history-response';
import { RejectedPaymentItemResponse } from '../models/responses/rejected-payment-item-response';
import { RejectionReferralUser } from '../models/responses/rejection-referral-user';
import { ActionRejectedPaymentItemRequest } from '../models/requests/action-rejected-payment-item-request';
import { RejectedPaymentItemsSummary } from '../models/responses/rejected-payment-items-summary';
import { PaymentItemError } from '../models/responses/payment-item-error';
import { VatVerification } from '../models/responses/vat-verification';
import { VatRequestType } from '../models/enums/vat-request-type.enum';
import { RejectBatchPaymentItemsRequest } from '../models/requests/reject-batch-payment-items-request';

export const finOpsActionPrefix = '[FinOps]';

export const loadChartOfAccounts = createAction(
  `${finOpsActionPrefix} Load Chart Of Accounts`,
);

export const loadChartOfAccountsSuccess = createAction(
  `${finOpsActionPrefix} Load Chart Of Accounts Success`,
  props<{ chartOfAccounts: AccountSummary[] }>(),
);

export const loadChartOfAccountsFailure = createAction(
  `${finOpsActionPrefix} Load Chart Of Accounts Failure`,
  props<{ error: string }>(),
);

export const exportChartOfAccounts = createAction(
  `${finOpsActionPrefix} Export Chart Of Accounts`,
);

export const exportChartOfAccountsSuccess = createAction(
  `${finOpsActionPrefix} Export Chart Of Accounts Success`,
  props<{ report: GeneratedReport }>(),
);

export const exportChartOfAccountsFailure = createAction(
  `${finOpsActionPrefix} Export Chart Of Accounts Failure`,
  props<{ error: string }>(),
);

export const downloadChartOfAccountsSuccess = createAction(
  `${finOpsActionPrefix} Download Chart Of Accounts Success`,
);

export const loadTrialBalanceAccounts = createAction(
  getActionName('Load Trial Balance Accounts', finOpsActionPrefix),
  props<{
    request: LoadTrialBalanceAccountsRequest;
  }>(),
);

export const loadTrialBalanceAccountsSuccess = createAction(
  getActionName('Load Trial Balance Accounts Success', finOpsActionPrefix),
  props<{ trialBalanceAccounts: TrialBalanceAccount[] }>(),
);

export const loadTrialBalanceAccountsFailure = createAction(
  getActionName('Load Trial Balance Accounts Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);

export const loadTrialBalanceTransactions = createAction(
  getActionName('Load Trial Balance Transactions', finOpsActionPrefix),
  props<{
    request: LoadTrialBalanceTransactionsRequest;
  }>(),
);

export const loadTrialBalanceTransactionsSuccess = createAction(
  getActionName('Load Trial Balance Transactions Success', finOpsActionPrefix),
);

export const loadTrialBalanceTransactionsFailure = createAction(
  getActionName('Load Trial Balance Transactions Failure', finOpsActionPrefix),
);

export const loadDetailedTrialBalanceTransactions = createAction(
  getActionName('Load Detailed Trial Balance Transactions', finOpsActionPrefix),
  props<{
    request: LoadDetailedTrialBalanceTransactionsRequest;
  }>(),
);

export const loadDetailedTrialBalanceTransactionsSuccess = createAction(
  getActionName(
    'Load Detailed Trial Balance Transactions Success',
    finOpsActionPrefix,
  ),
);

export const loadDetailedTrialBalanceTransactionsFailure = createAction(
  getActionName(
    'Load Detailed Trial Balance Transactions Failure',
    finOpsActionPrefix,
  ),
);

export const loadTrialBalanceTransactionsTotals = createAction(
  getActionName('Load Trial Balance Transactions Totals', finOpsActionPrefix),
  props<{
    request: LoadTrialBalanceTransactionsTotalsRequest;
  }>(),
);

export const loadTrialBalanceTransactionsTotalsSuccess = createAction(
  getActionName(
    'Load Trial Balance Transactions Totals Success',
    finOpsActionPrefix,
  ),
  props<{
    totals: TrialBalanceTransactionsTotals;
  }>(),
);

export const loadTrialBalanceTransactionsTotalsFailure = createAction(
  getActionName(
    'Load Trial Balance Transactions Totals Failure',
    finOpsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const loadTrialBalanceTransactionsFilters = createAction(
  getActionName('Load Trial Balance Transactions Filters', finOpsActionPrefix),
);

export const loadTrialBalanceTransactionsFiltersSuccess = createAction(
  getActionName(
    'Load Trial Balance Transactions Filters Success',
    finOpsActionPrefix,
  ),
  props<{
    filters: TrialBalanceTransactionsFilters;
  }>(),
);

export const loadTrialBalanceTransactionsFiltersFailure = createAction(
  getActionName(
    'Load Trial Balance Transactions Filter Failure',
    finOpsActionPrefix,
  ),
  props<{ error: string }>(),
);

export const exportTrialBalanceAccounts = createAction(
  `${finOpsActionPrefix} Export Trial Balance Accounts`,
  props<{
    startingPeriod: number;
    endingPeriod: number;
  }>(),
);

export const exportTrialBalanceAccountsSuccess = createAction(
  `${finOpsActionPrefix} Export Trial Balance Accounts Success`,
  props<{ report: GeneratedReport }>(),
);

export const exportTrialBalanceAccountsFailure = createAction(
  `${finOpsActionPrefix} Export Trial Balance Accounts Failure`,
  props<{ error: string }>(),
);

export const downloadTrialBalanceAccountsSuccess = createAction(
  `${finOpsActionPrefix} Download Trial Balance Accounts Success`,
);

export const exportTrialBalanceTransactions = createAction(
  `${finOpsActionPrefix} Export Trial Balance Transactions`,
  props<{ request: ExportTrialBalanceTransactionsRequest }>(),
);
export const exportTrialBalanceTransactionsSuccess = createAction(
  `${finOpsActionPrefix} Export Trial Balance Transactions Success`,
  props<{ report: GeneratedReport }>(),
);

export const exportTrialBalanceTransactionsFailure = createAction(
  `${finOpsActionPrefix} Export Trial Balance Transactions Failure`,
  props<{ error: string }>(),
);

export const downloadTrialBalanceTransactionsSuccess = createAction(
  `${finOpsActionPrefix} Download Trial Balance Transactions Success`,
);

export const loadMonthEndReadinessTable = (<T>() =>
  createAction(
    getActionName('Load Month End Readiness Table', finOpsActionPrefix),
    props<{
      request: LoadMonthEndReadinessTableRequest<T>;
    }>(),
  ))();

export const loadMonthEndReadinessTableSuccess = createAction(
  getActionName('Load Month End Readiness Table Success', finOpsActionPrefix),
);

export const loadMonthEndReadinessTableFailure = createAction(
  getActionName('Load Month End Readiness Table Failure', finOpsActionPrefix),
);

export const loadMonthEndReadinessCards = (<T>() =>
  createAction(
    getActionName('Load Month End Readiness Cards', finOpsActionPrefix),
    props<{
      request: LoadMonthEndReadinessCardsRequest<T>;
    }>(),
  ))();

export const loadMonthEndReadinessCardsSuccess = createAction(
  getActionName('Load Month End Readiness Cards Success', finOpsActionPrefix),
);

export const loadMonthEndReadinessCardsFailure = createAction(
  getActionName('Load Month End Readiness Cards Failure', finOpsActionPrefix),
);

export const loadUnmatchedBankTransactionsTable = createAction(
  getActionName('Load Unmatched Bank Transactions Table', finOpsActionPrefix),
  props<{
    request: AgGridRequest<UnmatchedBankTransaction>;
  }>(),
);

export const loadUnmatchedBankTransactionsTableSuccess = createAction(
  getActionName(
    'Load Unmatched Bank Transactions Table Success',
    finOpsActionPrefix,
  ),
);

export const loadUnmatchedBankTransactionsTableFailure = createAction(
  getActionName(
    'Load Unmatched Bank Transactions Table Failure',
    finOpsActionPrefix,
  ),
);

export const loadUnmatchedLedgerTransactionsTable = createAction(
  getActionName('Load Unmatched Ledger Transactions Table', finOpsActionPrefix),
  props<{
    request: AgGridRequest<UnmatchedLedgerTransaction>;
  }>(),
);

export const loadUnmatchedLedgerTransactionsTableSuccess = createAction(
  getActionName(
    'Load Unmatched Ledger Transactions Table Success',
    finOpsActionPrefix,
  ),
);

export const loadUnmatchedLedgerTransactionsTableFailure = createAction(
  getActionName(
    'Load Unmatched Ledger Transactions Table Failure',
    finOpsActionPrefix,
  ),
);

export const loadBankTransactionsFilters = createAction(
  getActionName('Load Bank Transactions Filters', finOpsActionPrefix),
);

export const loadBankTransactionsFiltersSuccess = createAction(
  getActionName('Load Bank Transactions Filters Success', finOpsActionPrefix),
  props<{
    filters: BankFilters;
  }>(),
);

export const loadBankTransactionsFiltersFailure = createAction(
  getActionName('Load Bank Transactions Filter Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);

export const loadLedgerTransactionsFilters = createAction(
  getActionName('Load Ledger Transactions Filters', finOpsActionPrefix),
);

export const loadLedgerTransactionsFiltersSuccess = createAction(
  getActionName('Load Ledger Transactions Filters Success', finOpsActionPrefix),
  props<{
    filters: LedgerFilters;
  }>(),
);

export const loadLedgerTransactionsFiltersFailure = createAction(
  getActionName('Load Ledger Transactions Filter Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);

export const reconcileUnmatchedTransactions = createAction(
  getActionName('Reconcile Unmatched Transactions', finOpsActionPrefix),
  props<{
    request: ReconcileTransactionsRequest;
    successCallBack: () => void;
  }>(),
);

export const reconcileUnmatchedTransactionsSuccess = createAction(
  getActionName('Reconcile Unmatched Transactions Success', finOpsActionPrefix),
  props<{
    response: TransactionReconciliationSummary;
  }>(),
);

export const reconcileUnmatchedTransactionsFailure = createAction(
  getActionName('Reconcile Unmatched Transactions Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);
export const exportMonthEndReadinessTable = createAction(
  getActionName('Export Month End Readiness Table', finOpsActionPrefix),
  props<{
    request: ExportMonthEndReadinessTableRequest;
  }>(),
);

export const exportMonthEndReadinessTableSuccess = createAction(
  getActionName('Export Month End Readiness Table Success', finOpsActionPrefix),
  props<{ report: GeneratedReport }>(),
);

export const exportMonthEndReadinessTableFailure = createAction(
  getActionName('Export Month End Readiness Table Failure', finOpsActionPrefix),
);

export const downloadMonthEndReadinessTableSuccess = createAction(
  getActionName(
    'Download Month End Readiness Export Success',
    finOpsActionPrefix,
  ),
);

export const clearReconciliationSummary = createAction(
  getActionName('Clear reconciliation summary', finOpsActionPrefix),
);
export const loadMatchedBankTransactionsTable = createAction(
  getActionName('Load Matched Bank Transactions Table', finOpsActionPrefix),
  props<{
    request: AgGridRequest<MatchedBankTransaction>;
  }>(),
);

export const loadMatchedBankTransactionsTableSuccess = createAction(
  getActionName(
    'Load Matched Bank Transactions Table Success',
    finOpsActionPrefix,
  ),
);

export const loadMatchedBankTransactionsTableFailure = createAction(
  getActionName(
    'Load Matched Bank Transactions Table Failure',
    finOpsActionPrefix,
  ),
);

export const loadMatchedLedgerTransactionsTable = createAction(
  getActionName('Load Matched Ledger Transactions Table', finOpsActionPrefix),
  props<{
    request: AgGridRequest<MatchedLedgerTransaction>;
  }>(),
);

export const loadMatchedLedgerTransactionsTableSuccess = createAction(
  getActionName(
    'Load Matched Ledger Transactions Table Success',
    finOpsActionPrefix,
  ),
);

export const loadMatchedLedgerTransactionsTableFailure = createAction(
  getActionName(
    'Load Matched Ledger Transactions Table Failure',
    finOpsActionPrefix,
  ),
);

export const loadMatchedTransactionsTable = createAction(
  getActionName('Load Matched Transactions Table', finOpsActionPrefix),
  props<{
    request: AgGridRequest<MatchedTransaction>;
  }>(),
);

export const loadMatchedTransactionsTableSuccess = createAction(
  getActionName('Load Matched Transactions Table Success', finOpsActionPrefix),
);

export const loadMatchedTransactionsTableFailure = createAction(
  getActionName('Load Matched Transactions Table Failure', finOpsActionPrefix),
);

export const loadTransactionReconciliation = createAction(
  getActionName('Load Transaction Reconciliation', finOpsActionPrefix),
  props<{
    id: number;
  }>(),
);

export const loadTransactionReconciliationSuccess = createAction(
  getActionName('Load Transaction Reconciliation Success', finOpsActionPrefix),
  props<{
    reconciliation: TransactionReconciliation;
  }>(),
);

export const loadTransactionReconciliationFailure = createAction(
  getActionName('Load Transaction Reconciliation Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);
export const clearTransactionReconciliation = createAction(
  getActionName('Clear Transaction Reconciliation', finOpsActionPrefix),
);
export const unmatchTransactions = createAction(
  getActionName('Unmatch Transactions', finOpsActionPrefix),
  props<{
    request: UnmatchTransactionsRequest;
    successCallBack: () => void;
  }>(),
);

export const unmatchTransactionsSuccess = createAction(
  getActionName('Unmatch Transactions Success', finOpsActionPrefix),
);

export const unmatchTransactionsFailure = createAction(
  getActionName('Unmatch Transactions Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);
export const loadTransactionReconciliationsTable = createAction(
  getActionName('Load Transaction Reconciliations Table', finOpsActionPrefix),
  props<{
    request: AgGridRequest<AllTransactionReconciliation>;
  }>(),
);

export const loadTransactionReconciliationsTableSuccess = createAction(
  getActionName(
    'Load Transaction Reconciliations Table Success',
    finOpsActionPrefix,
  ),
);

export const loadTransactionReconciliationsTableFailure = createAction(
  getActionName(
    'Load Transaction Reconciliations Table Failure',
    finOpsActionPrefix,
  ),
);

export const loadBankReconciliationSummary = createAction(
  getActionName('Load Bank Reconciliation Summary', finOpsActionPrefix),
  props<{ request: LoadBankReconciliationSummaryRequest }>(),
);

export const loadBankReconciliationSummarySuccess = createAction(
  getActionName('Load Bank Reconciliation Summary Success', finOpsActionPrefix),
  props<{ response: BankReconciliationSummary }>(),
);

export const loadBankReconciliationSummaryFailure = createAction(
  getActionName('Load Bank Reconciliation Summary Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);
export const exportUnmatchedTransactions = createAction(
  `${finOpsActionPrefix} Export Unmatched Transactions`,
);

export const exportUnmatchedTransactionsSuccess = createAction(
  `${finOpsActionPrefix} Export Unmatched Transactions Success`,
  props<{ report: GeneratedReport }>(),
);

export const exportUnmatchedTransactionsFailure = createAction(
  `${finOpsActionPrefix} Export Unmatched Transactions Failure`,
  props<{ error: string }>(),
);
export const downloadUnmatchedTransactionsSuccess = createAction(
  `${finOpsActionPrefix} Download Unmatched Transactions Success`,
);
export const loadMonthEndDates = createAction(
  `${finOpsActionPrefix} Load Month End Dates`,
);

export const loadMonthEndDatesSuccess = createAction(
  `${finOpsActionPrefix} Load Month End Dates Success`,
  props<{ monthEndDates: MonthEndDates[] }>(),
);

export const loadMonthEndDatesFailure = createAction(
  `${finOpsActionPrefix} Load Month End Dates Failure`,
  props<{ error: string }>(),
);
export const loadSummaryBankTransactionsTable = createAction(
  getActionName('Load Summary Bank Transactions Table', finOpsActionPrefix),
  props<{
    request: AgGridRequest<SummaryBankTransaction>;
  }>(),
);

export const loadSummaryBankTransactionsTableSuccess = createAction(
  getActionName(
    'Load Summary Bank Transactions Table Success',
    finOpsActionPrefix,
  ),
);

export const loadSummaryBankTransactionsTableFailure = createAction(
  getActionName(
    'Load Summary Bank Transactions Table Failure',
    finOpsActionPrefix,
  ),
);

export const loadSummaryLedgerTransactionsTable = createAction(
  getActionName('Load Summary Ledger Transactions Table', finOpsActionPrefix),
  props<{
    request: AgGridRequest<SummaryLedgerTransaction>;
  }>(),
);

export const loadSummaryLedgerTransactionsTableSuccess = createAction(
  getActionName(
    'Load Summary Ledger Transactions Table Success',
    finOpsActionPrefix,
  ),
);

export const loadSummaryLedgerTransactionsTableFailure = createAction(
  getActionName(
    'Load Summary Ledger Transactions Table Failure',
    finOpsActionPrefix,
  ),
);

export const loadBankReconciliationSummaryDrillThroughMetadata = createAction(
  getActionName(
    'Load Bank Reconciliation Summary Drill Through Metadata',
    finOpsActionPrefix,
  ),
  props<{
    request: LoadBankReconciliationSummaryDrillThroughMetadataRequest;
  }>(),
);

export const loadBankReconciliationSummaryDrillThroughMetadataSuccess =
  createAction(
    getActionName(
      'Load Bank Reconciliation Summary Drill Through Metadata Success',
      finOpsActionPrefix,
    ),
    props<{ response: SummaryDrillThroughMetadata }>(),
  );

export const loadBankReconciliationSummaryDrillThroughMetadataFailure =
  createAction(
    getActionName(
      'Load Bank Reconciliation Summary Drill Through Metadata Failure',
      finOpsActionPrefix,
    ),
    props<{ error: string }>(),
  );

export const setSummaryGlPeriod = createAction(
  getActionName('Set summary general ledger period', finOpsActionPrefix),
  props<{ period: number }>(),
);

export const loadUnearnedPremiumReserveReport = createAction(
  `${finOpsActionPrefix} Load Unearned Premium Reserve Report`,
  props<{
    request: LoadUnearnedPremiumReserveRequest;
  }>(),
);

export const loadUnearnedPremiumReserveReportSuccess = createAction(
  `${finOpsActionPrefix} Load Unearned Premium Reserve Report Success`,
  props<{ unearnedPremiumReserve: UnearnedPremiumReserve[] }>(),
);

export const loadUnearnedPremiumReserveReportFailure = createAction(
  `${finOpsActionPrefix} Load Unearned Premium Reserve Report Failure`,
  props<{ error: string }>(),
);

export const loadUnearnedPremiumReserveTotals = createAction(
  getActionName('Load Unearned Premium Reserve Totals', finOpsActionPrefix),
  props<{
    request: LoadUnearnedPremiumReserveTotalsRequest;
  }>(),
);

export const loadUnearnedPremiumReserveTotalsSuccess = createAction(
  getActionName(
    'Load Unearned Premium Reserve Totals Success',
    finOpsActionPrefix,
  ),
  props<{
    totals: UnearnedPremiumReserveTotals;
  }>(),
);

export const loadUnearnedPremiumReserveTotalsFailure = createAction(
  getActionName(
    'Load Unearned Premium Reserve Totals Failure',
    finOpsActionPrefix,
  ),
  props<{ error: string }>(),
);
export const exportSummaryTransactions = createAction(
  `${finOpsActionPrefix} Export Summary Transactions`,
  props<{ request: ExportBankReconciliationSummaryDrillThroughRequest }>(),
);

export const exportSummaryTransactionsSuccess = createAction(
  `${finOpsActionPrefix} Export Summary Transactions Success`,
  props<{ report: GeneratedReport }>(),
);

export const exportSummaryTransactionsFailure = createAction(
  `${finOpsActionPrefix} Export Summary Transactions Failure`,
  props<{ error: string }>(),
);
export const downloadSummaryTransactionsSuccess = createAction(
  `${finOpsActionPrefix} Download Summary Transactions Success`,
);
export const loadPostingRequests = createAction(
  getActionName('Load Posting Requests', finOpsActionPrefix),
  props<{
    request: AgGridRequest<AllPostingRequests>;
  }>(),
);
export const loadPostingRequestsSuccess = createAction(
  getActionName('Load Posting Requests Success', finOpsActionPrefix),
);

export const loadPostingRequestsFailure = createAction(
  getActionName('Load Posting Requests Failure', finOpsActionPrefix),
);

export const loadPostingDraftTransactions = createAction(
  getActionName('Load Posting Draft Transactions', finOpsActionPrefix),
  props<{
    request: AgGridRequest<AllPostingDraftTransactions>;
  }>(),
);
export const loadPostingDraftTransactionsSuccess = createAction(
  getActionName('Load Posting Draft Transactions Success', finOpsActionPrefix),
);

export const loadPostingDraftTransactionsFailure = createAction(
  getActionName('Load Posting Draft Transactions Failure', finOpsActionPrefix),
);

export const loadPostingLedgerTransactions = createAction(
  getActionName('Load Posting Ledger Transactions', finOpsActionPrefix),
  props<{
    request: AgGridRequest<AllPostingLedgerTransactions>;
  }>(),
);
export const loadPostingLedgerTransactionsSuccess = createAction(
  getActionName('Load Posting Ledger Transactions Success', finOpsActionPrefix),
);

export const loadPostingLedgerTransactionsFailure = createAction(
  getActionName('Load Posting Ledger Transactions Failure', finOpsActionPrefix),
);

export const loadPostingRequest = createAction(
  getActionName('Load Posting Request', finOpsActionPrefix),
  props<{
    id: number;
  }>(),
);

export const loadPostingRequestSuccess = createAction(
  getActionName('Load Posting Request Success', finOpsActionPrefix),
  props<{
    request: PostingRequest;
  }>(),
);

export const loadPostingRequestFailure = createAction(
  getActionName('Load Posting Request Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);

export const createPostingRequest = createAction(
  getActionName('Create Posting Request', finOpsActionPrefix),
  props<{
    request: CreatePostingRequest;
  }>(),
);

export const createPostingRequestSuccess = createAction(
  getActionName('Create Posting Request Success', finOpsActionPrefix),
);

export const createPostingRequestFailure = createAction(
  getActionName('Create Posting Request Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);

export const updatePostingRequest = createAction(
  getActionName('Update Posting Request', finOpsActionPrefix),
  props<{
    request: UpdatePostingRequest;
  }>(),
);

export const updatePostingRequestSuccess = createAction(
  getActionName('Update Posting Request Success', finOpsActionPrefix),
);

export const updatePostingRequestFailure = createAction(
  getActionName('Update Posting Request Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);
export const actionPostingRequest = createAction(
  getActionName('Action Posting Request', finOpsActionPrefix),
  props<{
    request: ActionPostingRequest;
  }>(),
);

export const actionPostingRequestSuccess = createAction(
  getActionName('Action Posting Request Success', finOpsActionPrefix),
);

export const actionPostingRequestFailure = createAction(
  getActionName('Action Posting Request Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);
export const getPostingMetadata = createAction(
  getActionName('Get Posting Metadata', finOpsActionPrefix),
);

export const getPostingMetadataSuccess = createAction(
  getActionName('Get Posting Metadata Success', finOpsActionPrefix),
  props<{
    response: PostingMetadata;
  }>(),
);

export const getPostingMetadataFailure = createAction(
  getActionName('Get Posting Metadata Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);

export const exportSummary = createAction(
  `${finOpsActionPrefix} Export Summary`,
  props<{ period: number }>(),
);
export const exportSummarySuccess = createAction(
  `${finOpsActionPrefix} Export Summary Success`,
  props<{ report: GeneratedReport }>(),
);

export const exportSummaryFailure = createAction(
  `${finOpsActionPrefix} Export Summary Failure`,
  props<{ error: string }>(),
);
export const downloadSummarySuccess = createAction(
  `${finOpsActionPrefix} Download Summary Success`,
);

export const loadOutstandingClaimReserveReport = createAction(
  `${finOpsActionPrefix} Load Outstanding Claim Reserve Report`,
  props<{
    request: LoadOutstandingClaimsReportRequest;
  }>(),
);

export const loadOutstandingClaimReserveReportSuccess = createAction(
  `${finOpsActionPrefix} Load Outstanding Claim Reserve Report Success`,
  props<{ outstandingClaimReserve: OutstandingClaimReserve[] }>(),
);

export const loadOutstandingClaimReserveReportFailure = createAction(
  `${finOpsActionPrefix} Load Outstanding Claim Reserve Report Failure`,
  props<{ error: string }>(),
);

export const loadClaimMovementReport = createAction(
  `${finOpsActionPrefix} Load Claim Movement Report`,
  props<{
    request: LoadClaimsMovementReportRequest;
  }>(),
);

export const loadClaimMovementReportSuccess = createAction(
  `${finOpsActionPrefix} Load Claim Movement Report Success`,
  props<{ claimMovement: ClaimMovement[] }>(),
);

export const loadClaimMovementReportFailure = createAction(
  `${finOpsActionPrefix} Load Claim Movement Report Failure`,
  props<{ error: string }>(),
);

export const loadClaimMovementTotals = createAction(
  getActionName('Load Claim Movement Totals', finOpsActionPrefix),
  props<{
    request: LoadClaimsMovementReportRequest;
  }>(),
);

export const loadClaimMovementTotalsSuccess = createAction(
  getActionName('Load Claim Movement Totals Success', finOpsActionPrefix),
  props<{
    totals: ClaimMovementTotals;
  }>(),
);

export const loadClaimMovementTotalsFailure = createAction(
  getActionName('Load Claim Movement Totals Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);

export const loadOutstandingClaimReserveTotalsFailure = createAction(
  getActionName(
    'Load Outstanding Claim Reserve Totals Failure',
    finOpsActionPrefix,
  ),
  props<{ error: string }>(),
);
export const reversePostingRequest = createAction(
  getActionName('Reverse Posting Request', finOpsActionPrefix),
  props<{
    request: ReversePostingRequest;
  }>(),
);

export const reversePostingRequestSuccess = createAction(
  getActionName('Reverse Posting Request Success', finOpsActionPrefix),
);

export const reversePostingRequestFailure = createAction(
  getActionName('Reverse Posting Request Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);
export const exportJournals = createAction(
  `${finOpsActionPrefix} Export Journals`,
  props<{ filters: PostingExportFilters }>(),
);

export const exportJournalsSuccess = createAction(
  `${finOpsActionPrefix} Export Journals Success`,
  props<{ report: GeneratedReport }>(),
);

export const exportJournalsFailure = createAction(
  `${finOpsActionPrefix} Export Journals Failure`,
  props<{ error: string }>(),
);
export const downloadJournalsSuccess = createAction(
  `${finOpsActionPrefix} Download Journals Success`,
);
export const loadPendingVatRequests = createAction(
  `[FinOps : VAT Verifications ] Load Pending VAT Requests`,
  props<{ requestType: VatRequestType }>(),
);
export const loadPendingVatRequestsSuccess = createAction(
  `[FinOps : VAT Verifications ] Load Pending VAT Requests Success`,
  props<{ pendingRequests: VatVerification[] }>(),
);
export const loadPendingVatRequestsFailure = createAction(
  `[FinOps : VAT Verifications ] Load Pending VAT Requests Failure`,
  props<{ error: string }>(),
);

export const exportVatVerifications = createAction(
  `[FinOps : VAT Verifications ] Export VAT Verifications`,
  props<{ verificationType: VatRequestType }>(),
);
export const exportVatVerificationsSuccess = createAction(
  `[FinOps : VAT Verifications ] Export VAT Verifications Success`,
  props<{ report: GeneratedReport }>(),
);
export const exportVatVerificationsFailure = createAction(
  `[FinOps : VAT Verifications ] Export VAT Verifications Failure`,
  props<{ error: string }>(),
);
export const downloadVatVerificationsSuccess = createAction(
  `[FinOps : VAT Verifications ] Download VAT Verifications Success`,
);

export const getLatestBankStatementDetails = createActionGroup({
  source: 'FinOps: Get Latest Bank Statement Details',
  events: {
    do: emptyProps(),
    success: props<{ details: LatestBankStatementDetails }>(),
    failure: props<{ error: string }>(),
  },
});

export const getOutstandingPaymentsDetailsForSubsystem = createActionGroup({
  source: 'FinOps: Get Subsystem Outstanding Payments Details',
  events: {
    do: props<{ subSystemCode: string }>(),
    success: props<{ details: OutstandingPaymentsDetails }>(),
    failure: props<{ error: string }>(),
  },
});

export const getOutstandingPaymentsDetails = createActionGroup({
  source: 'FinOps: Get Outstanding Payments Details',
  events: {
    do: emptyProps(),
    success: props<{ details: OutstandingPaymentsDetails }>(),
    failure: props<{ error: string }>(),
  },
});

export const getUnreleasedPayments = createActionGroup({
  source: 'FinOps: Load Unreleased Payments',
  events: {
    do: props<{
      request: AgGridRequest<Payment>;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const exportUnreleasedPayments = createActionGroup({
  source: 'FinOps: Export Unreleased Payments',
  events: {
    do: props<{
      request: AgGridRequest<Payment>;
      department: string;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const exportRejectedPaymentItems = createActionGroup({
  source: 'FinOps: Export Rejected Payment Items',
  events: {
    do: props<{
      request: AgGridRequest<Payment>;
      department: string;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const exportBankRejectionsPayments = createActionGroup({
  source: 'FinOps: Export Bank Rejected Payment Items',
  events: {
    do: props<{
      request: AgGridRequest<Payment>;
      department: string;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const exportReleaseOverviewPayments = createActionGroup({
  source: 'FinOps: Export Release Overview Payment Items',
  events: {
    do: props<{
      request: AgGridRequest<Payment>;
      department: string;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const requestPaymentsRelease = createActionGroup({
  source: 'FinOps: Request Payments Release',
  events: {
    do: props<{
      request: ReleasePaymentsRequest;
      successCallback: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const getGetOutstandingBatchDetails = createActionGroup({
  source: 'FinOps: Get Outstanding Batch Details',
  events: {
    do: props<{ request: GetOutstandingBatchDetails }>(),
    success: props<{ response: OutstandingBatchDetails }>(),
    failure: props<{ error: string }>(),
  },
});

export const getPaymentReasonCodes = createActionGroup({
  source: 'FinOps: Get Payment Reason Codes',
  events: {
    do: props<{ batchId?: number }>(),
    success: props<{ response: PaymentReasonCodeFilter[] }>(),
    failure: props<{ error: string }>(),
  },
});

export const getBatchSummary = createActionGroup({
  source: 'FinOps: Get Batch Summary',
  events: {
    do: emptyProps(),
    success: props<{ response: BatchSummary }>(),
    failure: props<{ error: string }>(),
  },
});

export const getBatchSummaryForDepartment = createActionGroup({
  source: 'FinOps: Get Batch Summary for Department',
  events: {
    do: props<{ subSystemCode: string; approvalStep: ApprovalStepEnum }>(),
    success: props<{ response: BatchDetails[] }>(),
    failure: props<{ error: string }>(),
  },
});

export const getBatchSummaryItems = createActionGroup({
  source: 'FinOps: Get Batch Summary Items',
  events: {
    do: props<{
      request: AgGridRequest<BatchSummaryItem>;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getBatchActionHistory = createActionGroup({
  source: 'FinOps: Get Batch Action History',
  events: {
    do: props<{ request: BatchActionHistoryRequest }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const actionPaymentBatch = createActionGroup({
  source: 'FinOps: Action Payment Batch',
  events: {
    do: props<{
      request: ActionPaymentReleaseApproval;
      successCallback: () => void;
      failureCallback?: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const getInProgressBatch = createActionGroup({
  source: 'FinOps: Get In Progress Batch',
  events: {
    do: emptyProps(),
    success: props<{ response: InProgressBatch }>(),
    failure: props<{ error: string }>(),
  },
});

export const getReleaseOverviewSummary = createActionGroup({
  source: 'FinOps: Get Release Summary Overview',
  events: {
    do: props<{ period: number }>(),
    success: props<{ response: ReleaseOverviewSummary }>(),
    failure: props<{ error: string }>(),
  },
});

export const getReleaseSummaryTransactions = createActionGroup({
  source: 'FinOps: Get Release Summary Transactions',
  events: {
    do: props<{ request: ReleaseOverviewTransactionRequest }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getReleaseOverviewPayments = createActionGroup({
  source: 'FinOps: Get Release Overview Payments',
  events: {
    do: props<{ request: ReleaseOverviewPaymentsRequest }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getBankRejectionSummary = createActionGroup({
  source: 'FinOps: Get Bank Rejection Summary',
  events: {
    do: props<{ glPeriod: number }>(),
    success: props<{ response: BankRejectionSummaryResponse }>(),
    failure: props<{ error: string }>(),
  },
});

export const getBankRejections = createActionGroup({
  source: 'FinOps: Get Bank Rejections',
  events: {
    do: props<{ request: AgGridRequest<BankRejectionResponse> }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getBankRejectionHistory = createActionGroup({
  source: 'FinOps: Get Bank Rejection History',
  events: {
    do: props<{
      batchId: number;
      successCallBack: (response: BankRejectionHistoryResponse[]) => void;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getLastBankRejectionHistory = createActionGroup({
  source: 'FinOps: Get Last Bank Rejection History',
  events: {
    do: props<{
      batchId: number;
      successCallBack: (response: BankRejectionHistoryResponse) => void;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getRejectedPaymentItems = createActionGroup({
  source: 'FinOps: Get Rejected Payment Items',
  events: {
    do: props<{
      request: AgGridRequest<RejectedPaymentItemResponse>;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getRejectionReferralUsers = createActionGroup({
  source: 'FinOps: Get Rejection Referral User',
  events: {
    do: props<{ subSystemCode: string }>(),
    success: props<{ response: RejectionReferralUser[] }>(),
    failure: props<{ error: string }>(),
  },
});

export const allocatePaymentRejection = createAction(
  getActionName('Allocate Payment Rejection', finOpsActionPrefix),
  props<{
    id: number;
    username: string;
  }>(),
);

export const allocatePaymentRejectionSuccess = createAction(
  getActionName('Allocate Payment Rejection Success', finOpsActionPrefix),
);

export const allocatePaymentRejectionFailure = createAction(
  getActionName('Allocate Payment Rejection Failure', finOpsActionPrefix),
  props<{ error: string }>(),
);

export const updateClaimPaymentRejectionsRowData = createAction(
  getActionName('Update Claim Payment Rejections Row Data', finOpsActionPrefix),
  props<{
    key: string;
    value: number | string;
    changes: Record<string, any>[];
  }>(),
);
export const updatePensionPaymentRejectionsRowData = createAction(
  getActionName(
    'Update Pension Payment Rejections Row Data',
    finOpsActionPrefix,
  ),
  props<{
    key: string;
    value: number | string;
    changes: Record<string, any>[];
  }>(),
);
export const updateUnderwritingPaymentRejectionsRowData = createAction(
  getActionName(
    'Update Underwriting Payment Rejections Row Data',
    finOpsActionPrefix,
  ),
  props<{
    key: string;
    value: number | string;
    changes: Record<string, any>[];
  }>(),
);
export const updateEapPaymentRejectionsRowData = createAction(
  getActionName('Update EAP Payment Rejections Row Data', finOpsActionPrefix),
  props<{
    key: string;
    value: number | string;
    changes: Record<string, any>[];
  }>(),
);

export const actionRejectedPaymentItem = createActionGroup({
  source: 'FinOps: Action Rejected Payment Item',
  events: {
    do: props<{
      request: ActionRejectedPaymentItemRequest;
      successCallback: () => void;
    }>(),
    success: props<{ decisionType: RejectedPaymentItemDecisionType }>(),
    failure: props<{ error: string }>(),
  },
});

export const actionRejectedPaymentItems = createActionGroup({
  source: 'FinOps: Action Rejected Payment Items',
  events: {
    do: props<{
      request: ActionRejectedPaymentItemsRequest;
      successCallback: () => void;
    }>(),
    success: props<{ decisionType: RejectedPaymentItemDecisionType }>(),
    failure: props<{ error: string }>(),
  },
});

export const getRejectedPaymentItemsSummary = createActionGroup({
  source: 'FinOps: Get Rejection Payment Items Summary',
  events: {
    do: emptyProps(),
    success: props<{ response: RejectedPaymentItemsSummary }>(),
    failure: props<{ error: string }>(),
  },
});

export const getPaymentItemErrors = createActionGroup({
  source: 'FinOps: Get Payment Item Errors',
  events: {
    do: props<{
      paymentItemId: number;
      successCallBack: (response: PaymentItemError[]) => void;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const resetRejectedPayment = createAction(
  `${finOpsActionPrefix} Clear Rejected Payment`,
);
export const loadRejectedPayment = createActionGroup({
  source: 'FinOps: Load Rejected Payment',
  events: {
    do: props<{
      paymentId: number;
      successCallBack: (response: RejectedPayment) => void;
    }>(),
    success: props<{ response: RejectedPayment }>(),
    failure: emptyProps(),
  },
});
export const getPaymentRejectionsReferralUsers = createActionGroup({
  source: 'FinOps: Get payment rejections referral users',
  events: {
    do: props<{
      paymentId: number;
      successCallBack: (response: PaymentRejectionReferralUser[]) => void;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getPaymentRejectionsHistory = createActionGroup({
  source: 'FinOps: Get Payment Rejections History',
  events: {
    do: props<{
      batchAuthId: number;
      successCallBack: (response: PaymentRejectionsAction[]) => void;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const rejectBatchPaymentItems = createActionGroup({
  source: 'FinOps: Reject Batch Payment Items',
  events: {
    do: props<{
      request: RejectBatchPaymentItemsRequest;
      successCallback: () => void;
    }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const loadPensionPaymentRejections = createAction(
  `${finOpsActionPrefix} Load Pension Payment Rejections`,
  props<{
    limit: number;
    offset: number;
    searchData: SearchRequest;
    complexSearchData: ComplexSearchRequest;
    sort: SortPropDir;
    isChecked: boolean;
    currentUser: string;
  }>(),
);

export const loadPensionPaymentRejectionsSuccess = createAction(
  `${finOpsActionPrefix} Load Pension Payment Rejections Success`,
  props<{ response: PagedApiData<PaymentRejections> }>(),
);

export const loadPensionPaymentRejectionsFailure = createAction(
  `${finOpsActionPrefix} Load Pension Payment Rejections Failure`,
  props<{ error: string }>(),
);

export const clearPensionPaymentRejections = createAction(
  `${finOpsActionPrefix} Clear Pension Payment Rejections`,
  props<{ maintainFilters: boolean }>(),
);

export const loadClaimPaymentRejections = createAction(
  `${finOpsActionPrefix} Load Claim Payment Rejections`,
  props<{
    limit: number;
    offset: number;
    searchData: SearchRequest;
    complexSearchData: ComplexSearchRequest;
    sort: SortPropDir;
    isChecked: boolean;
    currentUser: string;
  }>(),
);

export const loadClaimPaymentRejectionsSuccess = createAction(
  `${finOpsActionPrefix} Load Claim Payment Rejections Success`,
  props<{ response: PagedApiData<PaymentRejections> }>(),
);

export const loadClaimPaymentRejectionsFailure = createAction(
  `${finOpsActionPrefix} Load Claim Payment Rejections Failure`,
  props<{ error: string }>(),
);

export const clearClaimPaymentRejections = createAction(
  `${finOpsActionPrefix} Clear Claim Payment Rejections`,
  props<{ maintainFilters: boolean }>(),
);

export const setShowMyAllocatedPayments = createAction(
  `${finOpsActionPrefix} Set Show My Allocated Payments`,
  props<{ isChecked: boolean }>(),
);

export const exportPendingEftBatch = createActionGroup({
  source: 'FinOps: Export Pending Eft Batch',
  events: {
    do: props<{ batchId: number }>(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const loadRejectedPaymentEapDetails = createActionGroup({
  source: 'FinOps: Load Rejected Payment EAP Details',
  events: {
    do: props<{
      paymentId: number;
      successCallBack: (response: RejectedPaymentEapDetails) => void;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const startFinOpsNotifications = createActionGroup({
  source: `${finOpsActionPrefix} Start Fin Ops notification`,
  events: {
    do: emptyProps(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const startProcessingPaymentsNotifications = createActionGroup({
  source: `${finOpsActionPrefix} Create processing payment notification`,
  events: {
    do: emptyProps(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const updateProcessingPaymentsNotifications = createActionGroup({
  source: `${finOpsActionPrefix} Update processing payment notification`,
  events: {
    do: props<{ request: PaymentNotification }>(),
  },
});

export const startPaymentItemResubmissionCount = createActionGroup({
  source: `${finOpsActionPrefix} Create Payment Item Resubmission Count Notification`,
  events: {
    do: emptyProps(),
    success: emptyProps(),
    failure: props<{ error: string }>(),
  },
});

export const updatePaymentItemResubmissionCount = createActionGroup({
  source: `${finOpsActionPrefix} Update Payment Item Resubmission Count Notification`,
  events: {
    do: props<{ request: PaymentItemResubmissionCount }>(),
  },
});

export const loadEapPaymentRejections = createAction(
  `${finOpsActionPrefix} Load Eap Payment Rejections`,
  props<{
    limit: number;
    offset: number;
    searchData: SearchRequest;
    complexSearchData: ComplexSearchRequest;
    sort: SortPropDir;
    isChecked: boolean;
    currentUser: string;
  }>(),
);

export const loadEapPaymentRejectionsSuccess = createAction(
  `${finOpsActionPrefix} Load Eap Payment Rejections Success`,
  props<{ response: PagedApiData<PaymentRejections> }>(),
);

export const loadEapPaymentRejectionsFailure = createAction(
  `${finOpsActionPrefix} Load Eap Payment Rejections Failure`,
  props<{ error: string }>(),
);

export const clearEapPaymentRejections = createAction(
  `${finOpsActionPrefix} Clear Eap Payment Rejections`,
  props<{ maintainFilters: boolean }>(),
);

export const loadUnderwritingPaymentRejections = createAction(
  `${finOpsActionPrefix} Load Underwriting Payment Rejections`,
  props<{
    limit: number;
    offset: number;
    searchData: SearchRequest;
    complexSearchData: ComplexSearchRequest;
    sort: SortPropDir;
    isChecked: boolean;
    currentUser: string;
  }>(),
);

export const loadUnderwritingPaymentRejectionsSuccess = createAction(
  `${finOpsActionPrefix} Load Underwriting Payment Rejections Success`,
  props<{ response: PagedApiData<PaymentRejections> }>(),
);

export const loadUnderwritingPaymentRejectionsFailure = createAction(
  `${finOpsActionPrefix} Load Underwriting Payment Rejections Failure`,
  props<{ error: string }>(),
);

export const clearUnderwritingPaymentRejections = createAction(
  `${finOpsActionPrefix} Clear Underwriting Payment Rejections`,
  props<{ maintainFilters: boolean }>(),
);

export const getPaymentItemRejectionTypes = createActionGroup({
  source: `${finOpsActionPrefix} Get Item Rejection Types`,
  events: {
    do: emptyProps(),
    success: props<{ response: PaymentItemRejectionType[] }>(),
    failure: props<{ error: string }>(),
  },
});

export const getBankStatements = createActionGroup({
  source: 'FinOps: Load Bank Statements',
  events: {
    do: props<{
      request: LoadBankStatementRequest;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getBankStatementDetails = createActionGroup({
  source: 'FinOps: Load Bank Statement Detail',
  events: {
    do: props<{
      request: LoadBankStatementRequest;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const exportBankStatements = createActionGroup({
  source: 'FinOps: Export Bank Statements',
  events: {
    do: props<{
      request: LoadBankStatementRequest;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const exportBankStatementDetails = createActionGroup({
  source: 'FinOps: Export Bank Statement Details',
  events: {
    do: props<{
      request: LoadBankStatementRequest;
    }>(),
    success: emptyProps(),
    failure: emptyProps(),
  },
});

export const getRejectionModalSummary = createActionGroup({
  source: 'FinOps: Get Rejection Modal Summary',
  events: {
    do: props<{
      subSystem: string;
    }>(),
    success: props<{
      response: RejectionModalSummary;
    }>(),
    failure: props<{
      error: string;
    }>(),
  },
});
